import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Checkbox,
    Link,
    FormControlLabel,
    FormHelperText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Card,
    CardContent,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Skeleton,
    Avatar,
    Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CampaignIcon from "@mui/icons-material/Campaign";
import { campaignsDetails, getCoupons, createCampaign, updateCampaign, deleteCampaign, generateCampaignQR } from "../../../api/api";
import ImageWithHover from "../../../components/ImageWithHover/ImageWithHover";
import AdminHeader from "../../../components/AdminHeader/AdminHeader";
import Cookies from 'js-cookie';
import { formatAdminDate } from "../../../utils/formatDate";
import { getAdminNavLinks } from "../../../theme/adminNavLinks";
import { pageBackgroundSx } from "../../../theme/theme";
import { ADMIN_PAGE_GUIDES } from "../../../constants/adminPageGuides";

let swalLoaderPromise;
const getSwal = async () => {
    if (!swalLoaderPromise) {
        swalLoaderPromise = import("sweetalert2").then((module) => module.default);
    }
    return swalLoaderPromise;
};

// Styled components for better visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '6px 16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    },
}));

const CAMPAIGN_CODE_LENGTH = 6;
const CAMPAIGN_CODE_PATTERN = /^[A-Z0-9]{6}$/;

const formatCampaignCode = (value) =>
    value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, CAMPAIGN_CODE_LENGTH);

const getCampaignCodeError = (code) => {
    if (!code) return "Campaign Code is required";
    if (!CAMPAIGN_CODE_PATTERN.test(code)) {
        return `Use exactly ${CAMPAIGN_CODE_LENGTH} letters or numbers (A–Z, 0–9). No symbols.`;
    }
    return null;
};

const fieldHint = (hint, error) => error || hint;

const SectionLabel = ({ children }) => (
    <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: 'primary.main', mt: 1, mb: 0.5 }}
    >
        {children}
    </Typography>
);

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    borderRadius: 8,
    backgroundColor: '#ffffff',
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
    },
}));



const Index = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [isCouponsLoading, setIsCouponsLoading] = useState(true);
    const [form, setForm] = useState({
        name: "",
        partner: "",
        code: "",
        value_points: "",
        variable_points: false,
        day_selection: "",
        end_date: "",
        expire_days: "",
        signup_end_date: "",
        total_count: "",
        coupons: [],
        campaign_image: null,
    });
    const [errors, setErrors] = useState({});
    const [selectedDayOption, setSelectedDayOption] = useState(""); // date/days
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [qrData, setQrData] = useState(null);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrImageUrl, setQrImageUrl] = useState(null);
    const [campaignImagePreview, setCampaignImagePreview] = useState("");

    const showSwal = async (options) => {
        const Swal = await getSwal();
        return Swal.fire(options);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === "code") {
            const formattedCode = formatCampaignCode(value);
            setForm((prev) => ({ ...prev, code: formattedCode }));
            if (errors.code) {
                setErrors((prev) => ({ ...prev, code: getCampaignCodeError(formattedCode) }));
            }
            return;
        }
        
        if (name === "value_points") {
            // If points value is 0, uncheck variable points
            const pointsValue = parseFloat(value) || 0;
            setForm((prev) => ({
                ...prev,
                [name]: value,
                variable_points: pointsValue === 0 ? false : prev.variable_points
            }));
        } else {
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        }
    };

    const handleDaySelection = (e) => {
        const value = e.target.value;
        setSelectedDayOption(value);
        setForm({ ...form, day_selection: value });
    };

    const handleCouponCheck = (couponId) => {
        setForm((prev) => {
            const updated = prev.coupons.includes(couponId)
                ? prev.coupons.filter((id) => id !== couponId)
                : [...prev.coupons, couponId];
            return { ...prev, coupons: updated };
        });
    };

    const convertImageToWebp = (file, quality = 0.85) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const context = canvas.getContext("2d");

                if (!context) {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error("Failed to process image"));
                    return;
                }

                context.drawImage(img, 0, 0);
                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(objectUrl);
                        if (!blob) {
                            reject(new Error("Failed to convert image to WebP"));
                            return;
                        }

                        const fileName = file.name.replace(/\.[^/.]+$/, "") || "campaign-image";
                        resolve(
                            new File([blob], `${fileName}.webp`, {
                                type: "image/webp",
                                lastModified: Date.now(),
                            })
                        );
                    },
                    "image/webp",
                    quality
                );
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error("Failed to load selected image"));
            };

            img.src = objectUrl;
        });

    const handleCampaignImageChange = async (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            setForm((prev) => ({
                ...prev,
                campaign_image: null,
            }));
            setCampaignImagePreview("");
            return;
        }

        if (!file.type.startsWith("image/")) {
            setErrors((prev) => ({
                ...prev,
                campaign_image: "Please select a valid image file",
            }));
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setErrors((prev) => ({
                ...prev,
                campaign_image: "Image size should be less than 2MB",
            }));
            return;
        }

        try {
            const webpFile = await convertImageToWebp(file);
            if (webpFile.size > 2 * 1024 * 1024) {
                setErrors((prev) => ({
                    ...prev,
                    campaign_image: "Converted WebP image is still above 2MB. Please use a smaller image.",
                }));
                return;
            }

            const previewReader = new FileReader();
            previewReader.onloadend = () => {
                setCampaignImagePreview(previewReader.result);
            };
            previewReader.readAsDataURL(webpFile);

            setForm((prev) => ({
                ...prev,
                campaign_image: webpFile,
            }));

            if (errors.campaign_image) {
                setErrors((prev) => ({
                    ...prev,
                    campaign_image: null,
                }));
            }
        } catch (conversionError) {
            console.error("Campaign image conversion error:", conversionError);
            setErrors((prev) => ({
                ...prev,
                campaign_image: "Could not convert image to WebP. Please try another image.",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission behavior

        // ✅ Basic Validation
        const newErrors = {};
        if (!form.name) newErrors.name = "Campaign Name is required";
       // if (!form.partner) newErrors.partner = "Partner Name is required";
        const codeError = getCampaignCodeError(form.code);
        if (codeError) newErrors.code = codeError;
        if (!form.total_count)
            newErrors.total_count = "Total Count is required";
        if (!form.day_selection)
            newErrors.day_selection = "Day selection is required";
        if (form.day_selection === "date" && !form.end_date)
            newErrors.end_date = "End date is required";
        if (form.day_selection === "days" && !form.expire_days)
            newErrors.expire_days = "Expire days is required";
        // if (form.variable_points === false && !form.value_points)
        //     newErrors.value_points = "Points are required";
        if (form.coupons.length === 0)
            newErrors.coupons = "Please select at least one coupon";

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return; // Stop submission if errors exist
        }

        // ✅ Submit the form data
        try {
            console.log("Submitting form data:", form);

            // Get admin_company_id from cookies
            const admin_company_id = Cookies.get('admin_company_id');
            
            // Add admin_company_id to the form data
            const formDataWithCompany = {
                ...form,
                admin_company_id: admin_company_id
            };

            const res = await createCampaign(formDataWithCompany);
           // console.log("Submitted successfully", res.data);
            await showSwal({
                icon: 'success',
                title: 'Success!',
                text: 'Campaign created successfully',
                timer: 2000,
                showConfirmButton: false
            });
            
            // Reset form after successful submission
            setForm({
                name: "",
                partner: "",
                code: "",
                value_points: "",
                variable_points: false,
                day_selection: "",
                end_date: "",
                expire_days: "",
                total_count: "",
                signup_end_date: "",
                coupons: [],
                campaign_image: null,
            });
            setSelectedDayOption("");
            setErrors({});
            setCampaignImagePreview("");
            
            // Refresh campaigns list
            const response = await campaignsDetails();
            // Handle both paginated response { data: [...], pagination: {...} } and direct array
            const campaignsData = Array.isArray(response.data) 
                ? response.data 
                : (Array.isArray(response.data?.data) ? response.data.data : []);
            setCampaigns(campaignsData);
            
        } catch (error) {
            console.error("Error submitting form", error);
            
            let errorMessage = 'Error submitting campaign. Please try again.';
            
            if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
                errorMessage = 'Network connection error. Please check your internet connection and try again.';
            } else if (error.response) {
                // Server responded with error status
                if (error.response.status === 422) {
                    errorMessage = 'Please check your form data and try again.';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'Request timeout. Please try again.';
            }
            
            await showSwal({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        }
    };

    const handleEditCampaign = (campaign) => {
        setEditingCampaign(campaign);
        setForm({
            name: campaign.name,
            partner: campaign.partner || "",
            code: formatCampaignCode(campaign.code || ""),
            value_points: campaign.value_points || "",
            variable_points: campaign.variable_points || false,
            day_selection: campaign.day_selection || "",
            end_date: campaign.end_date || "",
            expire_days: campaign.expire_days || "",
            signup_end_date: campaign.signup_end_date || "",
            total_count: campaign.total_count || "",
            coupons: campaign.coupon_initials_ids ? campaign.coupon_initials_ids.split(',').map(id => parseInt(id)) : [],
            campaign_image: null,
        });
        setSelectedDayOption(campaign.day_selection || "");
        setCampaignImagePreview(campaign.campaign_image_url || "");
    };

    const handleDeleteCampaign = async (campaignId) => {
        const result = await showSwal({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteCampaign(campaignId);
                await showSwal({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Campaign has been deleted successfully.',
                    timer: 2000,
                    showConfirmButton: false
                });
                // Refresh campaigns list
                const response = await campaignsDetails();
                setCampaigns(response.data);
            } catch (error) {
                console.error('Error deleting campaign:', error);
                await showSwal({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Error deleting campaign: ' + (error.response?.data?.error || 'Unknown error'),
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const handleGenerateQR = async (campaignId) => {
        try {
            const response = await generateCampaignQR(campaignId);
           // console.log('QR response:', response.data);
            setQrData(response.data);
            
            // If QR code URL exists, show it immediately
            if (response.data.qr_code_url) {
              //  console.log('QR code URL:', response.data.qr_code_url);
                setQrImageUrl(response.data.qr_code_url);
                setQrModalOpen(true);
            } else {
                await showSwal({
                    icon: 'info',
                    title: 'QR Code Generated',
                    text: 'QR code data generated successfully. Click QR button again to view the QR code.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error generating QR code:', error);
            await showSwal({
                icon: 'error',
                title: 'Error!',
                text: 'Error generating QR code: ' + (error.response?.data?.error || 'Unknown error'),
                confirmButtonText: 'OK'
            });
        }
    };

    const closeQrModal = () => {
        setQrModalOpen(false);
        setQrImageUrl(null);
    };

    const handleUpdateCampaign = async (e) => {
        e.preventDefault();
        
        if (!editingCampaign) return;

        // Basic validation (same as create)
        const newErrors = {};
        if (!form.name) newErrors.name = "Campaign Name is required";
        const codeError = getCampaignCodeError(form.code);
        if (codeError) newErrors.code = codeError;
        if (!form.total_count) newErrors.total_count = "Total Count is required";
        if (!form.day_selection) newErrors.day_selection = "Day selection is required";
        if (form.day_selection === "date" && !form.end_date) newErrors.end_date = "End date is required";
        if (form.day_selection === "days" && !form.expire_days) newErrors.expire_days = "Expire days is required";
        if (form.coupons.length === 0) newErrors.coupons = "Please select at least one coupon";

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        try {
            await updateCampaign(editingCampaign.id, form);
            await showSwal({
                icon: 'success',
                title: 'Success!',
                text: 'Campaign updated successfully',
                timer: 2000,
                showConfirmButton: false
            });
            
            // Reset form and editing state
            setEditingCampaign(null);
            setForm({
                name: "",
                partner: "",
                code: "",
                value_points: "",
                variable_points: false,
                day_selection: "",
                end_date: "",
                signup_end_date: "",
                coupons: [],
                campaign_image: null,
            });
            setSelectedDayOption("");
            setErrors({});
            setCampaignImagePreview("");
            
            // Refresh campaigns list
            const response = await campaignsDetails();
            // Handle both paginated response { data: [...], pagination: {...} } and direct array
            const campaignsData = Array.isArray(response.data) 
                ? response.data 
                : (Array.isArray(response.data?.data) ? response.data.data : []);
            setCampaigns(campaignsData);
            
        } catch (error) {
            console.error("Error updating campaign:", error);
            await showSwal({
                icon: 'error',
                title: 'Error!',
                text: 'Error updating campaign. Please try again.',
                confirmButtonText: 'OK'
            });
        }
    };

    const cancelEdit = () => {
        setEditingCampaign(null);
        setForm({
            name: "",
            partner: "",
            code: "",
            value_points: "",
            variable_points: false,
            day_selection: "",
            end_date: "",
            expire_days: "",
            total_count: "",
            coupons: [],
            campaign_image: null,
        });
        setSelectedDayOption("");
        setErrors({});
        setCampaignImagePreview("");
    };

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const response = await campaignsDetails();
                // Handle both paginated response { data: [...], pagination: {...} } and direct array
                const campaignsData = Array.isArray(response.data) 
                    ? response.data 
                    : (Array.isArray(response.data?.data) ? response.data.data : []);
                setCampaigns(campaignsData);
            } catch (error) {
                console.error(error);
            }
        };
        const fetchCoupons = async () => {
            setIsCouponsLoading(true);
            try {
                const response = await getCoupons();
                setCoupons(response.data.coupons);
               // console.log(response.data, "coupon data");
            } catch (error) {
                console.error(error);
            } finally {
                setIsCouponsLoading(false);
            }
        };
        fetchCampaigns();
        fetchCoupons();
    }, []);

    return (
        <Box sx={{ 
            ...pageBackgroundSx,
            p: { xs: 1, sm: 1.5 },
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box',
        }}>
            <AdminHeader 
                title="Campaign Management"
                icon={CampaignIcon}
                navigationLinks={getAdminNavLinks("/add_campaign")}
                guide={ADMIN_PAGE_GUIDES.campaign}
            />

            {editingCampaign && (
                <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                    Editing <strong>{editingCampaign.name}</strong>. Update the fields below and click Update Campaign, or Cancel to discard changes.
                </Alert>
            )}

            <Grid container spacing={1.5}>
                {/* Left Side - Form */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <StyledCard>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Typography variant="subtitle1" sx={{ 
                                mb: 0.5, 
                                fontWeight: 700, 
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                <CampaignIcon sx={{ mr: 0.75, fontSize: 20 }} />
                                {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            
                            <Box component="form" noValidate autoComplete="off">
                                <SectionLabel>Campaign details</SectionLabel>
                                <Grid container spacing={1.5}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Campaign Name *"
                                            size="small"
                                            name="name"
                                            onChange={handleChange}
                                            value={form.name}
                                            error={!!errors.name}
                                            helperText={fieldHint('Name shown to users on the signup page.', errors.name)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Partner Name"
                                            size="small"
                                            name="partner"
                                            onChange={handleChange}
                                            value={form.partner}
                                            error={!!errors.partner}
                                            helperText={fieldHint('Optional — business or partner linked to this campaign.', errors.partner)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Campaign Code *"
                                            size="small"
                                            name="code"
                                            onChange={handleChange}
                                            value={form.code}
                                            placeholder="ABC123"
                                            error={!!errors.code}
                                            inputProps={{
                                                maxLength: CAMPAIGN_CODE_LENGTH,
                                                style: { fontFamily: 'monospace', letterSpacing: '0.1em', textTransform: 'uppercase' },
                                            }}
                                            helperText={fieldHint(
                                                'Exactly 6 letters or numbers, uppercase only (e.g. SUMR25). Used in QR signup links.',
                                                errors.code
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Total Count *"
                                            size="small"
                                            name="total_count"
                                            type="number"
                                            onChange={handleChange}
                                            value={form.total_count}
                                            error={!!errors.total_count}
                                            helperText={fieldHint('Max number of users who can join.', errors.total_count)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Signup End Date"
                                            name="signup_end_date"
                                            type="date"
                                            value={form.signup_end_date}
                                            size="small"
                                            onChange={handleChange}
                                            error={!!errors.signup_end_date}
                                            InputLabelProps={{ shrink: true }}
                                            helperText={fieldHint('Optional — last day new users can register.', errors.signup_end_date)}
                                        />
                                    </Grid>
                                </Grid>

                                <SectionLabel>Points &amp; usage rules</SectionLabel>
                                <Grid container spacing={1.5} alignItems="flex-start">
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={form.variable_points}
                                                    name="variable_points"
                                                    onChange={handleChange}
                                                    size="small"
                                                    sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                                                />
                                            }
                                            label={<Typography variant="body2">Variable Points</Typography>}
                                            sx={{ m: 0 }}
                                        />
                                        <FormHelperText sx={{ mt: 0, mx: 0 }}>
                                            Points come from each coupon instead of a fixed amount.
                                        </FormHelperText>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={form.one_time_use}
                                                    name="one_time_use"
                                                    onChange={handleChange}
                                                    size="small"
                                                    sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                                                />
                                            }
                                            label={<Typography variant="body2">One Time Use</Typography>}
                                            sx={{ m: 0 }}
                                        />
                                        <FormHelperText sx={{ mt: 0, mx: 0 }}>
                                            Each user can redeem campaign coupons only once.
                                        </FormHelperText>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            name="value_points"
                                            disabled={form.variable_points}
                                            label="Fixed Points"
                                            size="small"
                                            type="number"
                                            onChange={handleChange}
                                            value={form.value_points}
                                            error={!!errors.value_points}
                                            helperText={fieldHint(
                                                form.variable_points
                                                    ? 'Disabled when variable points is on.'
                                                    : 'Points awarded to each user who joins.',
                                                errors.value_points
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                <SectionLabel>Validity period</SectionLabel>
                                <Grid container spacing={1.5}>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <FormControl fullWidth size="small" error={!!errors.day_selection}>
                                            <InputLabel>Period Selection *</InputLabel>
                                            <StyledSelect
                                                name="day_selection"
                                                value={form.day_selection}
                                                onChange={handleDaySelection}
                                                label="Period Selection *"
                                            >
                                                <MenuItem value="date">Specific Date</MenuItem>
                                                <MenuItem value="days">Number of Days</MenuItem>
                                            </StyledSelect>
                                            <FormHelperText>
                                                {errors.day_selection || 'Choose a fixed end date or days-after-join expiry.'}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            name="end_date"
                                            type="date"
                                            label="End Date"
                                            disabled={selectedDayOption !== "date"}
                                            value={form.end_date}
                                            size="small"
                                            onChange={handleChange}
                                            error={!!errors.end_date}
                                            InputLabelProps={{ shrink: true }}
                                            helperText={fieldHint(
                                                selectedDayOption === 'date'
                                                    ? 'Campaign stops on this calendar date.'
                                                    : 'Only used when Period = Specific Date.',
                                                errors.end_date
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StyledTextField
                                            fullWidth
                                            name="expire_days"
                                            disabled={selectedDayOption !== "days"}
                                            value={form.expire_days}
                                            label="Expire Days"
                                            size="small"
                                            type="number"
                                            onChange={handleChange}
                                            error={!!errors.expire_days}
                                            helperText={fieldHint(
                                                selectedDayOption === 'days'
                                                    ? 'Days until expiry, counted from when each user joins.'
                                                    : 'Only used when Period = Number of Days.',
                                                errors.expire_days
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                <SectionLabel>Campaign image</SectionLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1,
                                            border: '1px dashed #cfcfcf',
                                            bgcolor: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {campaignImagePreview ? (
                                            <Box
                                                component="img"
                                                src={campaignImagePreview}
                                                alt="Campaign preview"
                                                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <CampaignIcon fontSize="small" color="disabled" />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 140 }}>
                                        <Button component="label" variant="outlined" size="small">
                                            Upload Image
                                            <input
                                                hidden
                                                type="file"
                                                accept="image/*"
                                                onChange={handleCampaignImageChange}
                                            />
                                        </Button>
                                        <FormHelperText sx={{ mt: 0.5, mx: 0 }}>
                                            {errors.campaign_image || 'Optional banner on the signup page. Max 2 MB, converted to WebP.'}
                                        </FormHelperText>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Right Side - Coupon Selection */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <StyledCard sx={{ height: '100%' }}>
                        <CardContent sx={{
                            p: { xs: 1.5, sm: 2 },
                            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}>
                            <Typography variant="subtitle1" sx={{ 
                                mb: 0.5, 
                                fontWeight: 700, 
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                <ReceiptIcon sx={{ mr: 0.75, fontSize: 20 }} />
                                Select Coupons *
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Check the coupons to issue when a user joins this campaign. At least one is required.
                            </Typography>
                            <Divider sx={{ mb: 1 }} />

                            <Box sx={{ flex: 1, maxHeight: { xs: 'min(50vh, 420px)', md: 480 }, overflowY: 'auto', minWidth: 0 }}>
                                {isCouponsLoading ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            py: 4,
                                            gap: 2,
                                        }}
                                    >
                                        <CircularProgress size={40} />
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "#666" }}
                                        >
                                            Loading coupons...
                                        </Typography>
                                        {/* Skeleton loaders matching coupon card structure */}
                                        <Box sx={{ width: "100%", mt: 2 }}>
                                            {[1, 2, 3, 4, 5].map((i) => (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        mb: 0.5,
                                                        p: 1,
                                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                                        borderRadius: 2,
                                                        backgroundColor: '#fafafa',
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                    >
                                                        <Box display="flex" alignItems="center" gap={2}>
                                                            <Skeleton
                                                                variant="circular"
                                                                width={40}
                                                                height={40}
                                                            />
                                                            <Skeleton
                                                                variant="text"
                                                                width={200}
                                                                height={24}
                                                            />
                                                        </Box>
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={24}
                                                            height={24}
                                                        />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : (
                                    coupons.map((coupon) => (
                                        <Box key={coupon.id} sx={{ 
                                            mb: 0.25, 
                                            px: 1,
                                            py: 0.5,
                                            border: '1px solid rgba(0, 0, 0, 0.08)', 
                                            borderRadius: 1, 
                                            backgroundColor: form.coupons.includes(coupon.id) ? '#e3f2fd' : '#fafafa',
                                            '&:hover': {
                                                backgroundColor: form.coupons.includes(coupon.id) ? '#bbdefb' : '#f5f5f5',
                                                borderColor: 'primary.main',
                                            }
                                        }}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ gap: 0.5, minWidth: 0 }}
                                            >
                                                <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: '1 1 auto' }}>
                                                    <ImageWithHover
                                                        icon={<ReceiptIcon sx={{ mr: 1, fontSize: 18, color: "primary.main" }} />}
                                                        imageUrl={coupon.coupon_image_url}
                                                    />
                                                    <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
                                                        {coupon.name}
                                                    </Typography>
                                                </Box>
                                                <Checkbox
                                                    size="small"
                                                    checked={form.coupons.includes(coupon.id)}
                                                    onChange={() => handleCouponCheck(coupon.id)}
                                                    sx={{
                                                        flexShrink: 0,
                                                        p: 0.5,
                                                        '&.Mui-checked': { color: 'primary.main' },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                            
                        {errors.coupons && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                                {errors.coupons}
                            </Typography>
                        )}

                            <Box sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                gap: 0.75,
                            }}>
                                <StyledButton
                                    variant="contained"
                                    size="small"
                                    onClick={editingCampaign ? handleUpdateCampaign : handleSubmit}
                                    sx={{ py: 0.5, px: 2, minHeight: 30, fontSize: '0.8125rem', width: 'auto' }}
                                >
                                    {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                                </StyledButton>
                                {editingCampaign && (
                                    <StyledButton
                                        variant="outlined"
                                        size="small"
                                        onClick={cancelEdit}
                                        sx={{ py: 0.5, px: 2, minHeight: 30, fontSize: '0.8125rem', width: 'auto' }}
                                    >
                                        Cancel
                                    </StyledButton>
                                )}
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid>

            {/* Campaign Details Table */}
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle1" sx={{ 
                    mb: 0.5, 
                    fontWeight: 700, 
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <CampaignIcon sx={{ mr: 0.75, fontSize: 20 }} />
                    Campaign Details
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    All active campaigns for your company. Use Edit to load a campaign into the form above, or QR to download a signup code.
                </Typography>
                
                <StyledCard>
                    <TableContainer sx={{ 
                        maxHeight: { xs: 360, sm: 480 },
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#a8a8a8',
                            },
                        },
                    }}>
                <Table sx={{ minWidth: 1200 }}>
                    <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            {[
                                "Campaign Name",
                                "Campaign Image",
                                "Partner Name",
                                "Issued Coupon",
                                "Points",
                                "Variable Points",
                                "Campaign Code",
                                "Day Selection",
                                "Expire Days",
                                "End Date",
                                "Signup End Date",
                                "Used/Total Count",
                                "Status",
                                "Actions",
                            ].map((header) => (
                                <TableCell
                                    sx={{
                                                fontWeight: 700,
                                                color: 'white',
                                                fontSize: '0.8rem',
                                                py: 1,
                                                whiteSpace: 'nowrap',
                                    }}
                                    key={header}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {campaigns.filter(campaign => campaign.campaign_type !== 'christmas').map((row, index) => (
                                    <TableRow 
                                        key={index}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                transition: 'background-color 0.2s ease-in-out',
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem', py: 0.75 }}>{row.name}</TableCell>
                                        <TableCell sx={{ py: 0.75 }}>
                                            {row.campaign_image_url ? (
                                                <Avatar
                                                    variant="rounded"
                                                    src={row.campaign_image_url}
                                                    alt={row.name || "Campaign"}
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{row.partner ?? "No Partner"}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{row.coupon_initials_ids}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem', py: 0.75 }}>{row.value_points ? row.value_points : "-"}</TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                            <Typography 
                                                sx={{ 
                                                    color: row.variable_points ? 'success.main' : 'text.secondary',
                                                    fontWeight: 500 
                                                }}
                                            >
                                                {row.variable_points ? 'Yes' : 'No'}
                                            </Typography>
                                </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.8rem', py: 0.75 }}>{row.code}</TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize', fontSize: '0.8rem', py: 0.75 }}>{row.day_selection}</TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{row.expire_days? row.expire_days : "-"}</TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{row.end_date ? formatAdminDate(row.end_date) : "No End Date"}</TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{formatAdminDate(row.signup_end_date)}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>
                                            {row.used_count || 0}/{row.total_count || 0}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75 }}>
                                            <Typography 
                                                sx={{ 
                                                    color: (row.used_count || 0) >= (row.total_count || 0) ? 'error.main' : 'success.main',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {(row.used_count || 0) >= (row.total_count || 0) ? 'Full' : 'Available'}
                                            </Typography>
                                        </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                    <Stack direction="row" spacing={0.5}>
                                                <StyledButton 
                                                    variant="contained" 
                                                    size="small" 
                                                    color="info"
                                                    onClick={() => handleEditCampaign(row)}
                                                >
                                                    Edit
                                                </StyledButton>
                                                <StyledButton 
                                                    variant="contained" 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => handleDeleteCampaign(row.id)}
                                                >
                                                    Delete
                                                </StyledButton>
                                                <StyledButton 
                                                    variant="contained" 
                                                    size="small" 
                                                    color="warning"
                                                    onClick={() => handleGenerateQR(row.id)}
                                                >
                                                    QR
                                                </StyledButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
                </StyledCard>
            </Box>

            {/* QR Code Modal */}
            <Dialog 
                open={qrModalOpen} 
                onClose={closeQrModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Campaign QR Code
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', py: 2 }}>
                    {qrData && (
                        <Box>
                            <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                                {qrData.campaign.name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                                Campaign Code: {qrData.campaign.code}
                            </Typography>
                            {qrImageUrl && (
                                <Box sx={{ mb: 3 }}>
                                    <img 
                                        src={qrImageUrl} 
                                        alt="QR Code" 
                                        loading="lazy"
                                        decoding="async"
                                        style={{ 
                                            maxWidth: '100%', 
                                            height: 'auto',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px'
                                        }}
                                        onError={(e) => {
                                            console.error('QR image failed to load:', qrImageUrl);
                                            e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                            console.log('QR image loaded successfully:', qrImageUrl);
                                        }}
                                    />
                                </Box>
                            )}
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Scan this QR code to join the campaign
                            </Typography>
                            <Typography variant="caption" sx={{ 
                                display: 'block', 
                                color: 'text.secondary',
                                wordBreak: 'break-all'
                            }}>
                                URL: {qrData.qr_data.url}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button 
                        onClick={closeQrModal} 
                        variant="contained"
                        sx={{ px: 4 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Index;
